import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  PlayCircle,
  Film,
  Users,
  Calendar,
  TrendingUp,
  DollarSign,
  CreditCard,
  Settings,
  Sparkles,
  RefreshCw,
  ShieldCheck,
  Clock as ClockIcon,
  Globe,
  ShoppingBag,
  FileText,
  Search,
  Plus,
  Bell,
  Coins,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  LogOut,
  Target,
  Menu,
  UserCircle,
  Wand2,
  Share2,
  X,
} from 'lucide-react';

/**
 * AppShell — the shared sidebar + topbar layout used by every logged-in page.
 *
 * Design goal (per user request 2026-07-06): blend of MasterClass (cinematic dark
 * canvas) + GoHighLevel (crisp SaaS chrome with collapsible left sidebar).
 * TOOLS section is visually distinguished with a gold divider + gold-tinted card.
 *
 * Layout:
 *   ┌─────────┬──────────────────────────────┐
 *   │ Sidebar │ Topbar (search / +/ credits) │
 *   │  (256)  ├──────────────────────────────┤
 *   │         │ children                     │
 *   └─────────┴──────────────────────────────┘
 *
 * Collapsible: sidebar shrinks to 64px width — icons only.
 * Mobile: sidebar becomes a slide-in drawer, hamburger in topbar.
 *
 * NOTE: This shell is opt-in. Pages must wrap themselves in <AppShell>...</AppShell>.
 * The old top-nav on non-AppShell pages continues to work unchanged.
 */
export default function AppShell({ children, active, title, breadcrumbs }) {
  // Admin "Login as" session marker — set by UsersTab before swapping tokens.
  const impersonation = (() => {
    try {
      return JSON.parse(sessionStorage.getItem('impersonation_user') || 'null');
    } catch {
      return null;
    }
  })();
  const endImpersonation = () => {
    const adminToken = sessionStorage.getItem('impersonation_return_token');
    sessionStorage.removeItem('impersonation_return_token');
    sessionStorage.removeItem('impersonation_user');
    if (adminToken) localStorage.setItem('token', adminToken);
    window.location.href = '/admin';
  };

  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = !!user?.is_admin;
  // Active key inference — pages can pass an explicit `active` prop, otherwise
  // we derive it from the current pathname's first segment.
  const activeKey = active || location.pathname.split('/')[1] || 'dashboard';

  const primaryNav = [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, to: '/dashboard' },
    { key: 'courses', label: 'Learn', icon: PlayCircle, to: '/courses' },
    { key: 'video-library', label: 'Video Library', icon: Film, to: '/video-library' },
    { key: 'ai-video-builder', label: 'AI Videos', icon: Wand2, to: '/ai-video-builder' },
    { key: 'directory', label: 'Community', icon: Users, to: '/directory' },
    { key: 'scheduler', label: 'Scheduler', icon: Calendar, to: '/scheduler' },
    { key: 'crm', label: 'CRM', icon: Target, to: '/crm' },
  ];
  const growthNav = [
    { key: 'commissions', label: 'Commissions', icon: DollarSign, to: '/dashboard#downline' },
    { key: 'statements', label: 'Statements', icon: FileText, to: '/statements' },
    { key: 'referrals', label: 'Referrals', icon: Share2, to: '/dashboard#downline' },
    { key: 'memberships', label: 'Memberships', icon: TrendingUp, to: '/memberships' },
    { key: 'billing', label: 'Profile', icon: UserCircle, to: '/profile/settings' },
  ];
  const adminTools = isAdmin
    ? [
        { key: 'ai-copilot', label: 'AI Copilot', icon: Sparkles, to: '/ai-assistant', dot: true },
        { key: 'stripe-sync', label: 'Stripe Sync', icon: RefreshCw, to: '/admin?tab=stripe-reconcile' },
        { key: 'verifications', label: 'Verifications', icon: ShieldCheck, to: '/admin?tab=verifications' },
        { key: 'audit-log', label: 'Audit Log', icon: ClockIcon, to: '/admin?tab=audit-log' },
        { key: 'edirectory', label: 'Directory', icon: Globe, to: '/admin?tab=edirectory' },
        { key: 'advertisers', label: 'Advertisers', icon: ShoppingBag, to: '/admin?tab=advertisers' },
        { key: 'legal', label: 'Legal Docs', icon: FileText, to: '/admin?tab=legal' },
      ]
    : [];

  const NavItem = ({ item, onClick }) => {
    const Icon = item.icon;
    const isActive = activeKey === item.key;
    return (
      <Link
        to={item.to}
        onClick={onClick}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors group ${
          isActive
            ? 'bg-white/10 text-white font-medium'
            : 'text-gray-400 hover:text-white hover:bg-white/5'
        }`}
        title={collapsed ? item.label : undefined}
        data-testid={`nav-${item.key}`}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        {!collapsed && (
          <>
            <span className="text-sm">{item.label}</span>
            {item.badge && (
              <span className="ml-auto text-[10px] bg-brand-blue/25 text-blue-200 px-1.5 py-0.5 rounded">
                {item.badge}
              </span>
            )}
          </>
        )}
      </Link>
    );
  };

  const ToolNavItem = ({ item, onClick }) => {
    const Icon = item.icon;
    return (
      <Link
        to={item.to}
        onClick={onClick}
        className="flex items-center gap-3 px-2.5 py-2 rounded-md text-brand-gold hover:bg-brand-gold/10 transition-colors group"
        title={collapsed ? item.label : undefined}
        data-testid={`nav-tool-${item.key}`}
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        {!collapsed && (
          <>
            <span className="text-sm font-medium">{item.label}</span>
            {item.dot && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
          </>
        )}
      </Link>
    );
  };

  const SidebarContent = ({ onNavClick }) => (
    <>
      <div className="h-16 flex items-center px-6 border-b border-white/5 flex-shrink-0">
        <Link to="/dashboard" className="font-serif text-xl font-bold tracking-wide text-white whitespace-nowrap">
          {collapsed ? (
            <span className="text-brand-gold">W</span>
          ) : (
            <>
              We<span className="text-brand-gold">Masterminds</span>
            </>
          )}
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto sidebar-scroll p-3 space-y-0.5">
        {!collapsed && (
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 mt-2 px-3">Main</div>
        )}
        {primaryNav.map((item) => (
          <NavItem key={item.key} item={item} onClick={onNavClick} />
        ))}

        {!collapsed && (
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 mt-6 px-3">Growth</div>
        )}
        {!collapsed || true ? (
          growthNav.map((item) => <NavItem key={item.key} item={item} onClick={onNavClick} />)
        ) : null}

        {/* TOOLS — admin-only, visually distinct */}
        {adminTools.length > 0 && (
          <div className="mt-6">
            {!collapsed && (
              <div className="flex items-center gap-2 mb-2 px-2">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-brand-gold/40 to-transparent" />
                <span className="text-[10px] font-bold text-brand-gold uppercase tracking-[0.15em]">◆ Tools</span>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent via-brand-gold/40 to-transparent" />
              </div>
            )}
            <div className={`${collapsed ? 'p-1' : 'p-2'} rounded-xl bg-gradient-to-b from-brand-gold/10 to-transparent border border-brand-gold/20 space-y-0.5 tools-glow`}>
              {adminTools.map((item) => (
                <ToolNavItem key={item.key} item={item} onClick={onNavClick} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* User footer + collapse toggle */}
      <div className="border-t border-white/5 flex-shrink-0">
        <div className="p-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-brand-gold/20 border border-brand-gold/40 flex items-center justify-center text-brand-gold font-semibold flex-shrink-0">
            {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{user?.name || 'Member'}</p>
              <p className="text-[11px] text-brand-gold truncate capitalize">
                {user?.membership_tier || (isAdmin ? 'Admin' : 'Member')}
              </p>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={() => { logout && logout(); navigate('/'); }}
              title="Sign out"
              className="w-8 h-8 rounded-md hover:bg-white/5 text-gray-400 hover:text-white flex items-center justify-center"
              data-testid="app-shell-logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="hidden md:flex w-full items-center justify-center gap-1 py-2 border-t border-white/5 text-[11px] text-gray-500 hover:text-white hover:bg-white/5"
          data-testid="app-shell-collapse-toggle"
        >
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <><ChevronLeft className="w-3 h-3" /> Collapse</>}
        </button>
      </div>
    </>
  );

  return (
    <div className="app-shell-root flex h-screen overflow-hidden bg-brand-bg text-white">
      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex ${collapsed ? 'w-16' : 'w-64'} bg-brand-surface border-r border-white/5 flex-col transition-[width] duration-200`}
        data-testid="app-shell-sidebar"
      >
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/60"
          onClick={() => setMobileOpen(false)}
          data-testid="app-shell-mobile-backdrop"
        />
      )}
      <aside
        className={`md:hidden fixed inset-y-0 left-0 w-64 z-50 bg-brand-surface border-r border-white/5 flex flex-col transition-transform duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent onNavClick={() => setMobileOpen(false)} />
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {impersonation && (
          <div
            className="bg-amber-500 text-black px-4 py-2 flex items-center justify-between gap-3 text-sm font-medium"
            data-testid="impersonation-banner"
          >
            <span className="truncate">
              Viewing as <strong>{impersonation.name}</strong> ({impersonation.email}) — impersonation session
            </span>
            <button
              onClick={endImpersonation}
              className="flex-shrink-0 px-3 py-1 rounded-md bg-black text-white text-xs font-semibold hover:bg-black/80 transition-colors"
              data-testid="return-to-admin-btn"
            >
              Return to Admin
            </button>
          </div>
        )}
        <header className="h-16 border-b border-white/5 bg-brand-bg/80 backdrop-blur-md flex items-center gap-4 px-4 md:px-6 sticky top-0 z-30">
          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden w-9 h-9 rounded-md hover:bg-white/5 flex items-center justify-center"
            data-testid="app-shell-mobile-open"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Back button — hidden on landing/dashboard roots, shown everywhere else */}
          {!['dashboard', 'home', ''].includes(activeKey) && (
            <button
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/dashboard'))}
              className="hidden md:inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors"
              title="Back"
              data-testid="app-shell-back"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}

          <div className="hidden md:block flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search…"
                className="w-full bg-white/5 border border-white/10 rounded-full py-1.5 pl-10 pr-4 text-sm text-white focus:outline-none focus:border-brand-gold/50 transition-colors"
                data-testid="app-shell-search"
              />
            </div>
          </div>

          {title && (
            <div className="hidden lg:block text-sm text-gray-400 md:hidden">
              <span className="text-white font-medium">{title}</span>
            </div>
          )}
          <div className="flex-1 md:hidden">
            <span className="font-serif text-lg text-white">
              We<span className="text-brand-gold">Masterminds</span>
            </span>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button
              className="hidden sm:flex px-3 py-1.5 rounded-full border border-brand-gold/30 bg-brand-gold/10 text-brand-gold text-xs font-medium items-center gap-1.5"
              data-testid="app-shell-credits"
            >
              <Coins className="w-3.5 h-3.5" />
              {(user?.ai_credits ?? 0).toLocaleString()} credits
            </button>
            <button
              className="w-9 h-9 rounded-full bg-brand-blue text-white flex items-center justify-center hover:bg-brand-blue/80 transition-colors"
              title="Quick add"
              data-testid="app-shell-quick-add"
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              className="w-9 h-9 rounded-md hover:bg-white/5 text-gray-400 flex items-center justify-center relative"
              title="Notifications"
              data-testid="app-shell-bell"
            >
              <Bell className="w-4 h-4" />
              <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-brand-gold" />
            </button>
          </div>
        </header>

        {breadcrumbs && (
          <div className="border-b border-white/5 px-6 py-2 text-xs text-gray-400">
            {breadcrumbs}
          </div>
        )}

        <main className="flex-1 overflow-y-auto bg-brand-bg">
          {children}
        </main>
      </div>
    </div>
  );
}
